import { useState } from 'react'
import { useTranslation } from '@/i18n'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'

interface Props {
  pidTags: string[]
  tsTags: string[]
  loading: boolean
  error: string | null
}

// Some "tags" come back as multi-paragraph OCR dumps from drawing legends
// (1800+ chars). Cap chip width and truncate to one line; full value is
// available on hover via IntuiTooltip.
const TagChip = ({ children }: { children: string }) => (
  <IntuiTooltip content={children} side="top">
    <span className="inline-block max-w-[16rem] truncate rounded-full border border-border bg-background px-2.5 py-1 text-xs text-foreground align-middle cursor-default">
      {children}
    </span>
  </IntuiTooltip>
)

const TagListPanel = ({ pidTags, tsTags, loading, error }: Props) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(true)

  return (
    <section className="rounded-lg border border-border bg-background dark:bg-dark-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <h2 className="text-sm font-semibold text-foreground">
          {t('match_entity:tag_list.title', { defaultValue: 'Tag List' })}
        </h2>
        {open ? (
          <ChevronUp className="size-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="size-4 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="grid grid-cols-2 gap-3 border-t border-border p-3">
          <TagColumn
            titleKey="match_entity:tag_list.pid_tags"
            defaultLabel="P&ID Tags"
            tags={pidTags}
            loading={loading}
            error={error}
          />
          <TagColumn
            titleKey="match_entity:tag_list.ts_tags"
            defaultLabel="Time Series Tags"
            tags={tsTags}
            loading={loading}
            error={error}
          />
        </div>
      )}
    </section>
  )
}

interface TagColumnProps {
  titleKey: string
  defaultLabel: string
  tags: string[]
  loading: boolean
  error: string | null
}

const INITIAL_TAGS_VISIBLE = 30

const TagColumn = ({ titleKey, defaultLabel, tags, loading, error }: TagColumnProps) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const visibleTags = expanded ? tags : tags.slice(0, INITIAL_TAGS_VISIBLE)
  const hiddenCount = tags.length - visibleTags.length

  return (
    <div className="min-w-0 rounded-md border border-border bg-background dark:bg-dark-card p-3 flex flex-col gap-2 min-h-[140px]">
      <h3 className="text-sm font-semibold text-foreground">
        {t(titleKey, { defaultValue: defaultLabel })}{' '}
        <span className="text-muted-foreground font-normal">({tags.length})</span>
      </h3>
      {loading ? (
        <p className="text-xs text-muted-foreground italic">
          {t('match_entity:tag_list.loading', { defaultValue: 'Loading tags…' })}
        </p>
      ) : error ? (
        <p className="text-xs text-destructive italic">{error}</p>
      ) : tags.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          {t('match_entity:tag_list.empty', { defaultValue: 'No tags available.' })}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {visibleTags.map((tag) => (
              <TagChip key={tag}>{tag}</TagChip>
            ))}
          </div>
          {tags.length > INITIAL_TAGS_VISIBLE && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="self-start text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
            >
              {expanded
                ? t('match_entity:tag_list.see_less', { defaultValue: 'See less' })
                : t('match_entity:tag_list.see_more', {
                    defaultValue: 'See more ({{count}})',
                    count: hiddenCount,
                  })}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default TagListPanel
